import { useFormContext } from "react-hook-form";
import { useGetFieldLabel } from "../../contexts/LanguageContext";
import {
  OFFLINE_TENTATIVE_DETAIL_FIELDS,
  isOfflineTentative,
} from "../../config/phaseConstants";
import { formatDate } from "../../utils/date";
import Input from "../CommonComponents/Input/Input";

/**
 * Longest value the two "No." columns accept (cp_applications
 * offline_demand_note_no / offline_certificate_no are varchar(100)).
 */
const OFFLINE_DETAIL_TEXT_MAX_LENGTH = 100;

/**
 * The four optional inputs an Offline Tentative applicant can fill about the
 * paper Demand Note and Tentative RL certificate they already hold. Meant to
 * be rendered inside the application form's grid, one grid cell per input,
 * and only while Application Type = Offline Tentative - the caller decides
 * that, so this component stays a plain list of inputs.
 *
 * None of them is mandatory. Dates are kept as the "YYYY-MM-DD" string the
 * date input itself produces, which is also how the backend stores and
 * returns them, so a saved draft shows its dates again without conversion.
 */
export function OfflineTentativeDetailFields({ disabled }: { disabled?: boolean }) {
  const { register } = useFormContext();
  const convert = useGetFieldLabel();

  return (
    <>
      {OFFLINE_TENTATIVE_DETAIL_FIELDS.map((field) => (
        <div key={`offline-tentative-${field.name}`}>
          <Input
            label={convert(field)}
            type={field.type}
            disabled={disabled ?? false}
            {...(field.type === "text" && { maxLength: OFFLINE_DETAIL_TEXT_MAX_LENGTH })}
            {...register(field.name)}
          />
        </div>
      ))}
    </>
  );
}

/**
 * Formats one of the four details for display: "-" when empty, DD/MM/YYYY for
 * the two dates, the text as typed for the two numbers.
 */
function formatOfflineTentativeDetail(
  application: Record<string, unknown> | null | undefined,
  field: (typeof OFFLINE_TENTATIVE_DETAIL_FIELDS)[number]
): string {
  const raw = application?.[field.name];
  if (raw === undefined || raw === null || raw === "") return "-";
  return field.type === "date" ? formatDate(String(raw)) : String(raw);
}

/**
 * The block a reviewer sees at the top of the Final Review screen for an
 * Offline Tentative application, in the place the Demand Note and payment
 * figures occupy for every other application: who and which plot the
 * application is for, followed by the four paper-document details. Laid out
 * as the same numbered two-column list as that payment block.
 *
 * The caller decides when to show it; this component only lays the rows out,
 * so Level 1, 2 and 3 can all use it.
 */
export function OfflineTentativeReviewSummary({
  application,
}: {
  application: Record<string, unknown> | null | undefined;
}) {
  const convert = useGetFieldLabel();
  const text = (key: string) => {
    const raw = application?.[key];
    return raw === undefined || raw === null || raw === "" ? "-" : String(raw);
  };

  const items: { label: string; value: string }[] = [
    { label: "Applicant Name", value: text("plot_owner_s_name") },
    { label: "Layout Name", value: text("unauthorized_layout_name") },
    { label: "Mauza", value: text("mauja") },
    { label: "Khasara", value: text("khasara_no") },
    { label: "Plot No", value: text("plot_no") },
    {
      label: "Plot Area",
      value: application?.area_of_plot
        ? `${application.area_of_plot} ${application.unit_area_of_plot || "sq. mt."}`
        : "-",
    },
    ...OFFLINE_TENTATIVE_DETAIL_FIELDS.map((field) => ({
      label: convert(field),
      value: formatOfflineTentativeDetail(application, field),
    })),
  ];

  const numbered = items.map((item, i) => ({ ...item, number: i + 1 }));
  const splitAt = Math.ceil(numbered.length / 2);

  const renderColumn = (columnItems: typeof numbered) => (
    <div className="basis-full md:basis-1/2 grow space-y-4">
      {columnItems.map((item) => (
        <div
          key={`offline-tentative-review-${item.number}`}
          className="flex items-center flex-wrap sm:flex-nowrap"
        >
          <p className="text-grey-600 text-xs sm:text-base font-bold shrink-0 sm:basis-1/2 mr-6">
            {`${item.number}. ${item.label}`}
          </p>
          <p className="text-grey-600 text-xs sm:text-base grow">{item.value}</p>
        </div>
      ))}
    </div>
  );

  return (
    <div className="flex flex-wrap md:flex-nowrap mb-4 gap-4">
      {renderColumn(numbered.slice(0, splitAt))}
      {renderColumn(numbered.slice(splitAt))}
    </div>
  );
}

/**
 * Read-only rows for the same four details, in the numbered "label : value"
 * layout the application view screens use for every other field. Renders
 * nothing for an application that is not Offline Tentative. `startIndex` is
 * the number of rows already printed above, so the numbering carries on.
 */
export function OfflineTentativeDetailsSummary({
  application,
  startIndex,
}: {
  application: Record<string, unknown> | null | undefined;
  startIndex: number;
}) {
  const convert = useGetFieldLabel();

  if (!isOfflineTentative(application)) return null;

  return (
    <>
      {OFFLINE_TENTATIVE_DETAIL_FIELDS.map((field, position) => {
        const value = formatOfflineTentativeDetail(application, field);

        return (
          <div
            key={`offline-tentative-summary-${field.name}`}
            className="flex items-center space-x-2 md:space-x-6 mb-2"
          >
            <p className="text-grey-600 text-sm md:text-base font-semibold shrink-0 basis-1/3">{`${
              startIndex + position + 1
            }. ${convert(field)}`}</p>
            <p className="text-grey-600 text-sm md:text-base shrink-0">:</p>
            <p className="text-grey-600 text-sm md:text-base grow inline-block min-w-[100px] text-wrap break-words">
              {value}
            </p>
          </div>
        );
      })}
    </>
  );
}
